import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { waitlistAPI } from "../../redux/api";
import { toast } from "../../Utills/toast";
import GlobalPopup from "../../component/Popup/GlobalPopup";
import { closeGlobalPopup, openGlobalPopup } from "../../redux/action";
import { useIsMobile } from "../../component/Hooks/useIsMobile";

const initialForm = { customerName: "", mobileNumber: "", numberOfGuests: 2, preferredSeating: "", estimatedWaitMinutes: 15, notes: "" };
const activeStatuses = ["Waiting", "Notified"];

function WaitlistFormFields({ form, updateField }) {
  return <div className="waitlist-form-fields"><label>Guest name<input name="customerName" required value={form.customerName} placeholder="Guest name" onChange={updateField} /></label><label>Mobile number<input name="mobileNumber" required value={form.mobileNumber} placeholder="Mobile number" onChange={updateField} /></label><label>Party size<input name="numberOfGuests" type="number" min="1" required value={form.numberOfGuests} onChange={updateField} /></label><label>Preferred seating<input name="preferredSeating" value={form.preferredSeating} placeholder="Indoor, outdoor, booth…" onChange={updateField} /></label><label>Estimated wait (minutes)<input name="estimatedWaitMinutes" type="number" min="0" required value={form.estimatedWaitMinutes} onChange={updateField} /></label></div>;
}

export default function WaitlistPanel() {
  const dispatch = useDispatch();
  const isMobile = useIsMobile(767);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showAddForm, setShowAddForm] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const refresh = useCallback(() => waitlistAPI().then(setEntries).catch(error => toast.error(error.message)), []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!selectedEntry) return undefined;
    const closeOnEscape = event => event.key === "Escape" && setSelectedEntry(null);
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [selectedEntry]);

  const active = useMemo(() => entries.filter(entry => activeStatuses.includes(entry.status)), [entries]);
  const history = useMemo(() => entries.filter(entry => !activeStatuses.includes(entry.status)), [entries]);
  const waitingCount = active.filter(entry => entry.status === "Waiting").length;
  const notifiedCount = active.filter(entry => entry.status === "Notified").length;
  const averageWait = active.length ? Math.round(active.reduce((total, entry) => total + Number(entry.estimatedWaitMinutes || 0), 0) / active.length) : 0;

  const updateField = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));
  const closeAddForm = () => setShowAddForm(false);
  const openAddForm = () => isMobile ? setShowAddForm(true) : dispatch(openGlobalPopup("add-walk-in"));
  const add = async event => {
    event.preventDefault();
    try {
      await waitlistAPI("", form, "POST");
      setForm(initialForm);
      if (isMobile) closeAddForm(); else dispatch(closeGlobalPopup());
      toast.success("Guest added to the waitlist.");
      refresh();
    } catch (error) { toast.error(error.message); }
  };
  const openActions = entry => { setSuggestions([]); setSelectedEntry(entry); };
  const findTable = async entry => {
    try {
      setSelectedEntry(entry);
      const tables = await waitlistAPI(`/${entry.waitlistEntryId}/suggestions`);
      setSuggestions(tables);
    } catch (error) { toast.error(error.message); }
  };
  const change = async (id, status) => {
    try {
      await waitlistAPI(`/${id}/status`, { status }, "POST");
      setSelectedEntry(null);
      toast.success(`Guest marked as ${status.toLowerCase()}.`);
      refresh();
    } catch (error) { toast.error(error.message); }
  };
  const seat = async (id, tableId) => {
    try {
      const result = await waitlistAPI(`/${id}/seat`, { restaurantTableId: tableId }, "POST");
      setSelectedEntry(null);
      toast.success(result.message);
      refresh();
    } catch (error) { toast.error(error.message); }
  };

  const entryCard = entry => <article className={`waitlist-entry status-${entry.status.toLowerCase()}`} key={entry.waitlistEntryId}>
    <div className="waitlist-entry-content">
      <div className="waitlist-entry-title"><strong>{entry.customerName}</strong><span className={`waitlist-status status-${entry.status.toLowerCase()}`}>{entry.status === "Notified" ? "Ready to seat" : entry.status}</span></div>
      <span>{entry.numberOfGuests} guests · {entry.mobileNumber}</span>
      <small>{entry.preferredSeating || "No seating preference"} · Est. {entry.estimatedWaitMinutes} min</small>
    </div>
    {activeStatuses.includes(entry.status) && <div className="waitlist-entry-actions"><button type="button" className="waitlist-seat-button" onClick={() => findTable(entry)}>Seat now</button><button type="button" className="waitlist-more-button" aria-label={`More actions for ${entry.customerName}`} onClick={() => openActions(entry)}>•••</button></div>}
  </article>;

  return <section className="waitlist-panel">
    <div className="waitlist-heading"><div><h2>Walk-in waitlist</h2><p>Keep arriving guests and available tables in sync.</p></div><button type="button" className="waitlist-add-button" onClick={openAddForm}>+ Add walk-in</button></div>

    <div className="waitlist-metrics" aria-label="Waitlist summary"><div><span>Waiting</span><strong>{waitingCount}</strong></div><div><span>Ready to seat</span><strong>{notifiedCount}</strong></div><div><span>Average wait</span><strong>{averageWait} min</strong></div></div>

    <div className="waitlist-list">
      {active.length ? active.map(entryCard) : <p className="waitlist-empty">No walk-in guests are waiting.</p>}
    </div>

    {history.length > 0 && <details className="waitlist-history" open={historyOpen} onToggle={event => setHistoryOpen(event.currentTarget.open)}><summary>History <span>{history.length}</span></summary><div className="waitlist-list">{history.map(entryCard)}</div></details>}

    <GlobalPopup popupKey="add-walk-in" title="Add walk-in guest" onClose={() => setForm(initialForm)} footer={(closePopup) => <><button type="button" className="popup-action-btn secondary" onClick={closePopup}>Cancel</button><button type="submit" form="waitlist-popup-form" className="popup-action-btn">Add to waitlist</button></>}><form id="waitlist-popup-form" className="waitlist-popup-form" onSubmit={add}><WaitlistFormFields form={form} updateField={updateField} /></form></GlobalPopup>

    {showAddForm && <div className="waitlist-sheet-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && closeAddForm()}><form className="waitlist-add-sheet" onSubmit={add} aria-label="Add walk-in guest">
      <div className="waitlist-sheet-handle" aria-hidden="true" />
      <div className="waitlist-sheet-heading"><div><p>Walk-in guest</p><h3>Add to waitlist</h3></div><button type="button" aria-label="Close add walk-in form" onClick={closeAddForm}>×</button></div>
      <WaitlistFormFields form={form} updateField={updateField} />
      <button type="submit" className="waitlist-submit-button">Add to waitlist</button>
    </form></div>}

    {selectedEntry && <div className="waitlist-sheet-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && setSelectedEntry(null)}><section className="waitlist-action-sheet" role="dialog" aria-modal="true" aria-labelledby={`waitlist-actions-${selectedEntry.waitlistEntryId}`}>
      <div className="waitlist-sheet-handle" aria-hidden="true" />
      <div className="waitlist-sheet-heading"><div><p>Guest actions</p><h3 id={`waitlist-actions-${selectedEntry.waitlistEntryId}`}>{selectedEntry.customerName}</h3></div><button type="button" aria-label="Close guest actions" onClick={() => setSelectedEntry(null)}>×</button></div>
      {suggestions.length > 0 && <div className="waitlist-table-suggestions"><strong>Available tables</strong>{suggestions.map(table => <button type="button" key={table.restaurantTableId} onClick={() => seat(selectedEntry.waitlistEntryId, table.restaurantTableId)}>Seat at T-{table.tableName} · {table.capacity} seats</button>)}</div>}
      {suggestions.length === 0 && <div className="waitlist-action-options"><button type="button" className="waitlist-seat-button" onClick={() => findTable(selectedEntry)}>Find a suitable table</button>{selectedEntry.status === "Waiting" && <button type="button" onClick={() => change(selectedEntry.waitlistEntryId, "Notified")}>Notify guest</button>}<button type="button" onClick={() => change(selectedEntry.waitlistEntryId, "Skipped")}>Skip for now</button><button type="button" className="waitlist-action-danger" onClick={() => change(selectedEntry.waitlistEntryId, "Cancelled")}>Cancel waitlist entry</button></div>}
    </section></div>}
  </section>;
}
